/**
 * Правка виз-блоков: конверсия styleConfig в новую форму
 * (`visualizations[]` + вычистка legacy-ключей), CRUD блоков и «прокси»-маппинг
 * для существующих секций панели (они читают/пишут legacy-корень одного блока).
 * См. docs/layers/plan.md и docs/layers/ui.md.
 */
import { EDITOR_DRAW_DEFAULT_HEX } from '../constants/editorPrimaryMapHex.js';
import type { LayerStyleConfig, LayerVisualization, VisualizationKind } from '../types/layer.js';
import { getDefaultPaintForType } from './detectGeometryType.js';
import {
  resolveVisualizations,
  visualizationKindGeometry,
  type GeometryCategory,
} from './resolveVisualizations.js';

/** paint-ключ цвета для kind'ов, где createDefaultVizBlock зовёт getDefaultPaintForType. */
const COLOR_KEY_BY_KIND: Partial<Record<VisualizationKind, string>> = {
  circle: 'circle-color',
  line: 'line-color',
  fill: 'fill-color',
  outline: 'line-color',
};

/**
 * Пер-виз ключи legacy-корня. Зеркало резолвера shared + панельные ключи,
 * которых shared-резолвер не знает (iconSizePx, previewAnchor).
 */
export const PANEL_PER_VIZ_KEYS = [
  'colorByProperty',
  'colorPalette',
  'colorMapping',
  'colorFallback',
  'colorByNumericRanges',
  'iconByProperty',
  'iconMapping',
  'previewParamName',
  'previewShape',
  'previewSize',
  'previewBorderWidth',
  'previewFreeBackground',
  'previewFit',
  'previewAnchor',
  'previewAnchorByPage',
  'showFieldOfView',
  'fieldOfViewAngleProperty',
  'showSymbolRotation',
  'symbolRotationAngleProperty',
  'heightByProperty',
  'heightMultiplier',
  'heightFallback',
  'iconSizePx',
  // Вид линии (docs/editing/plan.md §4.2.1): без ключей здесь правка из панели ушла бы в
  // корень styleConfig, а не в блок, и рендер её не увидел бы.
  'lineDash',
  'lineJoin',
  'lineStartCap',
  'lineEndCap',
  'linePattern',
  'lineSmoothness',
  'linePatternGap',
] as const;

/** Раскраска по параметру — переносится при смене kind (кроме heatmap). */
const COLOR_BY_KEYS = [
  'colorByProperty',
  'colorPalette',
  'colorMapping',
  'colorFallback',
  'colorByNumericRanges',
] as const;

const LEGACY_ROOT_KEYS = new Set<string>([
  'type',
  'paint',
  'layout',
  'styleByType',
  ...PANEL_PER_VIZ_KEYS,
]);

type StyleConfigLike = Record<string, unknown>;

/**
 * Минимум от GeoJSON FeatureCollection, который нужен авто-блоку: типы геометрий объектов.
 * Структурный тип вместо `GeoJSON.FeatureCollection`: в shared нет `@types/geojson`, а
 * настоящая коллекция из браузера и строки из БД на сервере под него подходят обе.
 */
interface GeometryCollectionLike {
  type?: string;
  features?: ReadonlyArray<{ geometry?: { type?: string } | null }>;
}

/** Ключи, копирование которых bracket-присваиванием в новый `{}` — proto-pollution surface. */
const DANGEROUS_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/** Тонкая обёртка над shared-резолвером с приведением StyleConfigLike. */
function resolveBlocks(
  styleConfig: StyleConfigLike | undefined,
  geometries?: ReadonlySet<GeometryCategory>,
): LayerVisualization[] {
  return resolveVisualizations(
    styleConfig as unknown as LayerStyleConfig | undefined,
    { geometries },
  );
}

/**
 * styleConfig в новой форме: `visualizations[]` — источник правды, legacy-ключи
 * вычищены, layer-level ключи (showLabels, minzoom, showCoordinates…) сохранены.
 * Панельные пер-виз ключи (iconSizePx, previewAnchor) доносятся до блока
 * активного legacy-типа вручную (shared-резолвер их не переносит).
 */
export function toVisualizationsForm(
  styleConfig: StyleConfigLike | undefined,
  geometries?: ReadonlySet<GeometryCategory>,
): StyleConfigLike {
  const sc = styleConfig ?? {};
  const alreadyNewForm = Array.isArray(sc.visualizations);
  const blocks = resolveBlocks(sc, geometries).map((block) => ({ ...block }));

  if (!alreadyNewForm) {
    const legacyKind = ((sc.type as string) || 'circle') as VisualizationKind;
    const target = blocks.find((block) => block.kind === legacyKind);
    if (target) {
      const t = target as unknown as Record<string, unknown>;
      for (const key of ['iconSizePx', 'previewAnchor'] as const) {
        if (sc[key] !== undefined && t[key] === undefined) {
          t[key] = sc[key];
        }
      }
    }
  }

  const out: StyleConfigLike = {};
  for (const [key, value] of Object.entries(sc)) {
    if (!DANGEROUS_KEYS.has(key) && !LEGACY_ROOT_KEYS.has(key) && key !== 'visualizations') {
      out[key] = value;
    }
  }
  out.visualizations = blocks;
  return out;
}

/**
 * Записывает блоки в styleConfig. Порядок массива = z-порядок рендера (низ→верх),
 * он авторитетный и сохраняется как есть — ручной reorder не перетирается.
 */
function withBlocks(
  base: StyleConfigLike,
  blocks: LayerVisualization[],
): StyleConfigLike {
  return { ...base, visualizations: blocks };
}

/**
 * Канонический дефолт-ранг kind для ВСТАВКИ нового блока (низ→верх): теплокарта
 * снизу, точки/символы сверху. Дальше пользователь может двигать блоки вручную.
 */
const CANONICAL_KIND_ORDER: readonly VisualizationKind[] = [
  'heatmap',
  'fill',
  'fill-extrusion',
  'outline',
  'line',
  'circle',
  'symbol',
  'preview-point',
  'sign',
];

function canonicalKindRank(kind: VisualizationKind): number {
  const idx = CANONICAL_KIND_ORDER.indexOf(kind);
  return idx === -1 ? CANONICAL_KIND_ORDER.length : idx;
}

/**
 * Дефолтный новый блок данного kind (paint/layout как у сегодняшних дефолтов).
 *
 * `getDefaultPaintForType` красит в `EDITOR_PRIMARY_LIGHT_MAP_HEX` — это legacy
 * render-fallback для СУЩЕСТВУЮЩИХ данных без явного цвета (см. комментарий в
 * editorPrimaryMapHex.ts), не дефолт для НОВОГО. Новый блок красим в
 * `EDITOR_DRAW_DEFAULT_HEX` — так же, как создание нового слоя в editorLayerConfigs.ts.
 *
 * Красим ВСЕ видимые части блока, а не только основную: у полигона это ещё и контур,
 * иначе он один остаётся в legacy-зелёном.
 *
 * `color` — для слоёв со своим цветом по умолчанию (слой «Объекты» режима «Навигация»
 * красится в цвет графа); остальным достаётся индиго.
 */
export function createDefaultVizBlock(
  kind: VisualizationKind,
  color: string = EDITOR_DRAW_DEFAULT_HEX,
): LayerVisualization {
  const paint: Record<string, unknown> =
    kind === 'circle' || kind === 'line' || kind === 'fill'
      ? getDefaultPaintForType(kind)
      : kind === 'outline'
        ? getDefaultPaintForType('line')
        : {};
  const colorKey = COLOR_KEY_BY_KIND[kind];
  if (colorKey) {
    paint[colorKey] = color;
  }
  if (kind === 'fill') {
    // Контур полигона рисуется отдельным line-слоем, и без явного цвета он падал в
    // legacy-фолбэк `EDITOR_PRIMARY_LIGHT_MAP_HEX`: свежий полигон выходил с индиговой
    // заливкой и ЗЕЛЁНОЙ обводкой, тогда как свежая линия — целиком индиговой
    // (docs/editing/bugs.md §16.1). Ключ именно `fill-outline-color` — это то, что
    // показывает и правит «Цвет обводки» в панели.
    paint['fill-outline-color'] = color;
  }
  const layout: Record<string, unknown> =
    kind === 'symbol' ? { 'icon-image': 'marker' } : {};
  return {
    id: randomVizBlockId(),
    kind,
    enabled: true,
    paint,
    layout,
  };
}

/**
 * Id нового блока. Модуль общий для браузера и сервера, а `packages/shared` собирается без
 * DOM- и node-типов, поэтому Web Crypto достаём из `globalThis` с проверкой: он есть и в
 * браузерах, и в Node 22.
 */
function randomVizBlockId(): string {
  const webCrypto = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  return `viz-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Снимает флаг «блок ещё не выбран» (docs/editing/plan.md §2.1) — вызывается из ЛЮБОЙ
 * РУЧНОЙ операции панели над блоками (добавил/удалил/сменил тип). После этого `[]`
 * снова значит «ничего не рисовать», а не «выведи блоки из данных заново» — иначе
 * удалённая пользователем визуализация воскресала бы при следующей загрузке.
 *
 * Пишем именно `false`, а не удаляем ключ: слои, созданные ДО этой фичи, никогда не
 * имели `autoVisualization` вовсе (`undefined`) и уже полагались на авто-добавление
 * блока под новую геометрию при рисовании — терять это для них не нужно. Отличить
 * «флага никогда не было» (`undefined`, старый слой — авто-добавление разрешено) от
 * «пользователь осознанно снял» (`false`, новый слой — запрещено) можно только явным
 * значением.
 *
 * НЕ вызывается из `addVizBlock` напрямую: та же функция используется и авто-путём
 * (первая нарисованная геометрия), которому флаг как раз нужно СОХРАНИТЬ как `true`.
 */
export function clearAutoVisualizationFlag(styleConfig: StyleConfigLike): StyleConfigLike {
  if (styleConfig.autoVisualization === false) {
    return styleConfig;
  }
  return { ...styleConfig, autoVisualization: false };
}

/**
 * Добавляет дефолт-блок нового kind в его канонической позиции (по z-рангу),
 * не трогая ручной порядок остальных блоков. No-op, если kind уже есть.
 * `color` — цвет нового блока, см. `createDefaultVizBlock`.
 */
export function addVizBlock(
  styleConfig: StyleConfigLike | undefined,
  geometries: ReadonlySet<GeometryCategory> | undefined,
  kind: VisualizationKind,
  color?: string,
): StyleConfigLike {
  const base = toVisualizationsForm(styleConfig, geometries);
  const blocks = base.visualizations as LayerVisualization[];
  if (blocks.some((block) => block.kind === kind)) {
    return base;
  }
  const newBlock = createDefaultVizBlock(kind, color);
  const rank = canonicalKindRank(kind);
  // Вставляем перед первым блоком с большим рангом (выше по z), иначе — в конец.
  let insertAt = blocks.findIndex((b) => canonicalKindRank(b.kind) > rank);
  if (insertAt === -1) {
    insertAt = blocks.length;
  }
  const next = [...blocks.slice(0, insertAt), newBlock, ...blocks.slice(insertAt)];
  return withBlocks(base, next);
}

/**
 * Двигает блок по z среди блоков ТОЙ ЖЕ геометрии (точки/линии/полигоны):
 * `up` — выше (позже в массиве), `down` — ниже. Кросс-геометрийный порядок не
 * меняется. No-op, если двигать некуда.
 */
export function moveVizBlock(
  styleConfig: StyleConfigLike | undefined,
  geometries: ReadonlySet<GeometryCategory> | undefined,
  vizId: string,
  direction: 'up' | 'down',
): StyleConfigLike {
  const base = toVisualizationsForm(styleConfig, geometries);
  const blocks = [...(base.visualizations as LayerVisualization[])];
  const index = blocks.findIndex((b) => b.id === vizId);
  if (index === -1) {
    return base;
  }
  const geom = visualizationKindGeometry(blocks[index]!.kind);
  const step = direction === 'up' ? 1 : -1;
  // Двигаем только на соседнюю позицию той же геометрии — иначе блок
  // «перепрыгнёт» через блок другой геометрии и поменяет относительный
  // порядок с ним, ломая кросс-геометрийную гарантию.
  const target = index + step;
  if (target < 0 || target >= blocks.length || visualizationKindGeometry(blocks[target]!.kind) !== geom) {
    return base;
  }
  [blocks[index], blocks[target]] = [blocks[target]!, blocks[index]!];
  return withBlocks(base, blocks);
}

/** Удаляет виз-блок по id. */
export function removeVizBlock(
  styleConfig: StyleConfigLike | undefined,
  geometries: ReadonlySet<GeometryCategory> | undefined,
  vizId: string,
): StyleConfigLike {
  const base = toVisualizationsForm(styleConfig, geometries);
  const blocks = base.visualizations as LayerVisualization[];
  return clearAutoVisualizationFlag(
    withBlocks(
      base,
      blocks.filter((block) => block.id !== vizId),
    ),
  );
}

/** Включает/выключает блок (выключенный не рендерится, настройки сохраняются). */
export function setVizBlockEnabled(
  styleConfig: StyleConfigLike | undefined,
  geometries: ReadonlySet<GeometryCategory> | undefined,
  vizId: string,
  enabled: boolean,
): StyleConfigLike {
  const base = toVisualizationsForm(styleConfig, geometries);
  const blocks = base.visualizations as LayerVisualization[];
  return withBlocks(
    base,
    blocks.map((block) => (block.id === vizId ? { ...block, enabled } : block)),
  );
}

/**
 * Смена способа показа внутри геометрии: paint/layout — дефолты нового kind,
 * раскраска по параметру переносится (кроме heatmap), id блока стабилен
 * (не сбрасывает expand-состояние карточки).
 */
export function changeVizBlockKind(
  styleConfig: StyleConfigLike | undefined,
  geometries: ReadonlySet<GeometryCategory> | undefined,
  vizId: string,
  newKind: VisualizationKind,
): StyleConfigLike {
  const base = toVisualizationsForm(styleConfig, geometries);
  const blocks = base.visualizations as LayerVisualization[];
  return clearAutoVisualizationFlag(
    withBlocks(
      base,
      blocks.map((block) => {
        if (block.id !== vizId || block.kind === newKind) {
          return block;
        }
        if (visualizationKindGeometry(block.kind) !== visualizationKindGeometry(newKind)) {
          return block;
        }
        const template = createDefaultVizBlock(newKind);
        const next: LayerVisualization = {
          ...template,
          id: block.id,
          enabled: block.enabled,
        };
        if (newKind !== 'heatmap') {
          const source = block as unknown as Record<string, unknown>;
          const target = next as unknown as Record<string, unknown>;
          for (const key of COLOR_BY_KEYS) {
            if (source[key] !== undefined) {
              target[key] = source[key];
            }
          }
        }
        return next;
      }),
    ),
  );
}

const GEOJSON_TYPE_TO_CATEGORY: Record<string, GeometryCategory> = {
  Point: 'point',
  MultiPoint: 'point',
  LineString: 'line',
  MultiLineString: 'line',
  Polygon: 'polygon',
  MultiPolygon: 'polygon',
};

const DEFAULT_KIND_FOR_GEOMETRY: Record<GeometryCategory, VisualizationKind> = {
  point: 'circle',
  line: 'line',
  polygon: 'fill',
};

/**
 * Авто-блок при появлении новой геометрии (нарисовали линию в точечном слое):
 * возвращает новый styleConfig с дефолт-блоком или null, если делать нечего.
 * Работает ТОЛЬКО для новой формы: legacy generic-слои получают блок от
 * резолвера по данным автоматически, а «оживлять» legacy-спецтипы нельзя
 * (паритет поведения до первого редактирования панелью).
 * `color` — цвет нового блока, см. `createDefaultVizBlock`.
 */
export function styleConfigWithAutoBlockForGeometry(
  styleConfig: StyleConfigLike | undefined,
  featureGeometryType: string | undefined,
  geojson: GeometryCollectionLike | null | undefined,
  color?: string,
): StyleConfigLike | null {
  const sc = styleConfig ?? {};
  if (!Array.isArray(sc.visualizations)) {
    return null;
  }
  // `false` — пользователь осознанно снял флаг ручной правкой блоков (см.
  // clearAutoVisualizationFlag): дальше решение за ним, авто-добавление больше не
  // включается. `undefined` (слой создан до этой фичи и флага никогда не имел) и
  // `true` — авто-добавление разрешено (см. комментарий в clearAutoVisualizationFlag).
  if (sc.autoVisualization === false) {
    return null;
  }
  const category = featureGeometryType
    ? GEOJSON_TYPE_TO_CATEGORY[featureGeometryType]
    : undefined;
  if (!category) {
    return null;
  }
  const blocks = sc.visualizations as LayerVisualization[];
  if (blocks.some((block) => visualizationKindGeometry(block.kind) === category)) {
    return null;
  }
  const geometries = new Set<GeometryCategory>();
  for (const feature of geojson?.features ?? []) {
    const cat = feature.geometry?.type
      ? GEOJSON_TYPE_TO_CATEGORY[feature.geometry.type]
      : undefined;
    if (cat) {
      geometries.add(cat);
    }
  }
  geometries.add(category);
  return addVizBlock(sc, geometries, DEFAULT_KIND_FOR_GEOMETRY[category], color);
}

/**
 * Legacy-styleConfig одного блока для существующих секций панели:
 * корень = layer-level ключи слоя + type/paint/layout и пер-виз ключи блока.
 */
export function synthesizeLegacyStyleConfigForBlock(
  styleConfig: StyleConfigLike | undefined,
  viz: LayerVisualization,
): StyleConfigLike {
  const sc = styleConfig ?? {};
  const out: StyleConfigLike = {};
  for (const [key, value] of Object.entries(sc)) {
    if (!DANGEROUS_KEYS.has(key) && !LEGACY_ROOT_KEYS.has(key) && key !== 'visualizations') {
      out[key] = value;
    }
  }
  out.type = viz.kind;
  out.paint = viz.paint ?? {};
  out.layout = viz.layout ?? {};
  const source = viz as unknown as Record<string, unknown>;
  for (const key of PANEL_PER_VIZ_KEYS) {
    if (source[key] !== undefined) {
      out[key] = source[key];
    }
  }
  return out;
}

/**
 * Обратный маппинг правки секции: секция вернула legacy-styleConfig прокси —
 * пер-виз ключи уходят патчем в блок, layer-level ключи — в корень новой формы.
 * `'key' in update` (а не `!== undefined`): явное `key: undefined` снимает ключ блока.
 */
export function applyProxyStyleConfigUpdate(
  styleConfig: StyleConfigLike | undefined,
  geometries: ReadonlySet<GeometryCategory> | undefined,
  vizId: string,
  legacyUpdate: StyleConfigLike,
): StyleConfigLike {
  const base = toVisualizationsForm(styleConfig, geometries);
  const blocks = base.visualizations as LayerVisualization[];

  const layerLevel: StyleConfigLike = {};
  for (const [key, value] of Object.entries(legacyUpdate)) {
    if (!DANGEROUS_KEYS.has(key) && !LEGACY_ROOT_KEYS.has(key) && key !== 'visualizations') {
      layerLevel[key] = value;
    }
  }

  const nextBlocks = blocks.map((block) => {
    if (block.id !== vizId) {
      return block;
    }
    const next = { ...block } as unknown as Record<string, unknown>;
    if ('paint' in legacyUpdate) {
      next.paint = legacyUpdate.paint ?? {};
    }
    if ('layout' in legacyUpdate) {
      next.layout = legacyUpdate.layout ?? {};
    }
    for (const key of PANEL_PER_VIZ_KEYS) {
      if (key in legacyUpdate) {
        if (legacyUpdate[key] === undefined) {
          delete next[key];
        } else {
          next[key] = legacyUpdate[key];
        }
      }
    }
    return next as unknown as LayerVisualization;
  });

  return { ...base, ...layerLevel, visualizations: nextBlocks };
}
